import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Download,
  Music2,
  Calendar,
  CheckCircle,
  Loader2,
  ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PurchasesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchPurchases = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/purchases`);
        setPurchases(response.data);
      } catch (error) {
        console.error('Error fetching purchases:', error);
        toast.error('Failed to load purchases');
      } finally {
        setLoading(false);
      }
    };

    fetchPurchases();
  }, [authLoading, isAuthenticated, navigate]);

  const handleDownload = async (purchase) => {
    setDownloading(purchase.id);
    try {
      // Choose the right backend endpoint based on license type
      let endpoint = `${BACKEND_URL}/api/purchases/${purchase.id}/download`;
      if (purchase.license_type === 'lyrics') {
        endpoint = `${BACKEND_URL}/api/purchases/${purchase.id}/download-lyrics`;
      }
      const response = await axios.get(endpoint, { responseType: 'blob' });
      
      // Check if it's a blob (actual file) or JSON (info response)
      if (response.data.type && response.data.type.includes('application/json')) {
        const text = await response.data.text();
        const data = JSON.parse(text);
        if (!data.download_available) {
          toast.info(data.message || 'File not available yet');
          return;
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const fallback = purchase.license_type === 'lyrics' ? 'lyrics.txt' : 'track.mp3';
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
        : fallback;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed.');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadContract = async (purchase) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/purchases/${purchase.id}/contract`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : 'Ziki_Store_Contract.pdf';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Contract downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download contract');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getLicenseLabel = (type) => {
    const labels = {
      sync: 'Sync License',
      commercial: 'Commercial License',
      nft: 'NFT/Token License'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark pt-24 pb-32">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
            My Collection
          </span>
          <h1 className="text-3xl font-medium text-white">Licensed Tracks</h1>
          <p className="text-slate-400 mt-2">
            Manage and download your purchased music licenses
          </p>
        </div>

        {/* Purchases List */}
        {purchases.length === 0 ? (
          <div className="bg-gradient-card border border-white/10 p-12 rounded-xl text-center">
            <Music2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-white mb-2">No Licenses Yet</h2>
            <p className="text-slate-400 mb-6">
              Browse the marketplace to find and license amazing tracks
            </p>
            <Button
              onClick={() => navigate('/marketplace')}
              className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold rounded-lg"
              data-testid="browse-marketplace-btn"
            >
              Browse Marketplace
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="purchases-list">
            {purchases.map((purchase) => (
              <div
                key={purchase.id}
                className="bg-gradient-card border border-white/10 p-6 rounded-xl hover:border-white/20 transition-colors"
                data-testid={`purchase-item-${purchase.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Track Info */}
                  <div className="flex items-center gap-4 flex-1">
                    {purchase.track ? (
                      <>
                        <img
                          src={purchase.track.artwork_url}
                          alt={purchase.track.title}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <div>
                          <h3 className="text-lg font-medium text-white">
                            {purchase.track.title}
                          </h3>
                          <p className="text-slate-400">{purchase.track.artist_name}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs font-medium rounded-full">
                              {getLicenseLabel(purchase.license_type)}
                            </span>
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <Calendar className="w-3 h-3" />
                              {formatDate(purchase.created_at)}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className="w-20 h-20 bg-slate-800 rounded-lg flex items-center justify-center">
                          <Music2 className="w-8 h-8 text-slate-600" />
                        </div>
                        <div>
                          <p className="text-slate-400">Track unavailable</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status & Actions */}
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-lg font-semibold text-white">
                        {formatPrice(purchase.amount)}
                      </p>
                      <div className="flex items-center gap-1 text-emerald-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Completed
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleDownload(purchase)}
                        disabled={downloading === purchase.id}
                        className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 rounded-lg"
                        data-testid={`download-btn-${purchase.id}`}
                      >
                        {downloading === purchase.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            {purchase.license_type === 'lyrics' ? 'Lyrics' : 'Download'}
                          </>
                        )}
                      </Button>
                      {purchase.license_type === 'exclusive' && (
                        <Button
                          variant="outline"
                          onClick={() => handleDownloadContract(purchase)}
                          className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-lg"
                          data-testid={`contract-btn-${purchase.id}`}
                          title="Download exclusive rights contract"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Contract
                        </Button>
                      )}
                      {purchase.license_type !== 'exclusive' && (
                        <Button
                          variant="outline"
                          onClick={() => handleDownloadContract(purchase)}
                          className="border-white/20 text-slate-300 hover:bg-white/5 rounded-lg"
                          data-testid={`agreement-btn-${purchase.id}`}
                          title="Download license agreement PDF"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Agreement
                        </Button>
                      )}
                      {purchase.track && (
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/tracks/${purchase.track.id}`)}
                          className="border-white/20 text-white hover:bg-white/5 rounded-lg"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
